import {
  ClientProperties,
  CoordinateSystemInfoProvider,
  CustomGamesResourceCache,
  IAuthorizationHolder,
  LocalizationInfoProvider,
  Rectangle,
  RootCanvasManager,
  ScaleEntry,
  ScaleManager,
  SceneCommon,
  SceneFactory,
  Stretch,
  T_IAuthorizationHolder,
  T_IClientProperties,
  T_ICoordinateSystemInfoProvider,
  T_ScaleManager,
  SCENE_COMMON_TOKEN,
  SCENE_FACTORY_TOKEN,
} from '@cgs/common';
import { BaseReelsSlotGame, GameProvider } from '@cgs/machines';
import {
  ApplicationGameConfig,
  ApplicationUserConfig,
  LocalStorageService,
  STORAGE_MARSHAL_SERVICE_TOKEN,
  STORAGE_SERVICE_TOKEN,
  StorageMarshalBaseService,
} from '@cgs/shared';
import {
  ApplicationBuilder,
  Container,
  InstanceLifetime,
  IResumeGamePopupComponent,
  PlatformSettings,
  RESUME_GAME_POPUP_COMPONENT_TOKEN,
  SCENE_BUILDER_TOKEN,
  T_Platform,
  T_ResourceCache,
  TemplateApplication,
  Vector2,
} from '@cgs/wge';
import { ILoadingScreen, LOADING_SCREEN_TOKEN } from '../loading-screen/loading-screen';
import { APP_SETTINGS_SERVICE_TOKEN, AppSettingsService } from './services/app-settings.service';
import {
  ISlotGameSettings,
  SLOT_GAME_SETTINGS_TOKEN,
  SlotGameSettings,
} from './slot-game-settings';
import { GAME_DATA_SERVICE_TOKEN, GameDataService } from '../services/game-data.service';

export const SLOT_GAME_BUILDER_TOKEN = Symbol('SLOT_GAME_BUILDER_TOKEN');

export class SlotGameBuilder extends ApplicationBuilder {
  protected preloader: ILoadingScreen;

  protected gameView: BaseReelsSlotGame | null = null;

  constructor(
    window: Window,
    canvas: HTMLCanvasElement,
    settings: PlatformSettings,
    private slotSettings: ISlotGameSettings,
    protected container: Container,
    private gameFactory: (machineId: string) => BaseReelsSlotGame,
    private gameProvider: GameProvider
  ) {
    super(window, canvas, settings, container);

    this.container.registerValue(SLOT_GAME_BUILDER_TOKEN, this);

    this.container.register(
      T_ICoordinateSystemInfoProvider,
      () => new CoordinateSystemInfoProvider(this.platform),
      { lifetime: InstanceLifetime.Singleton }
    );

    this.container.registerValue(T_Platform, this.platform);

    this.container.register(T_IClientProperties, () => new ClientProperties(), {
      lifetime: InstanceLifetime.Singleton,
    });
  }

  public build(): Promise<void> {
    const rect = this.container.resolve<CoordinateSystemInfoProvider>(
      T_ICoordinateSystemInfoProvider
    ).coordinateSystem;

    return super.build(rect);
  }

  public async onAppBuild(): Promise<void> {
    this.initResourceCache();
    this.initSlotGameSettings();
    this.gameProvider.register();
  }

  public async loadInitGameData(): Promise<void> {
    this.initScaleManager();

    await this.initPreloader();
  }

  private initResourceCache() {
    const localizeProvider = new LocalizationInfoProvider();

    this.container.registerValue(
      T_ResourceCache,
      new CustomGamesResourceCache(this.container, localizeProvider, this.resourceCache),
      { lifetime: InstanceLifetime.Singleton }
    );

    this.container.registerValue(SCENE_BUILDER_TOKEN, this.sceneBuilder, {
      lifetime: InstanceLifetime.Singleton,
    });

    this.container.registerValue(
      SCENE_FACTORY_TOKEN,
      new SceneFactory(this.sceneBuilder, this.container.resolve(T_ResourceCache)),
      { lifetime: InstanceLifetime.Singleton }
    );
  }

  private initSlotGameSettings() {
    const resourceUrl =
      this.container
        .resolve<CustomGamesResourceCache>(T_ResourceCache)
        .getUrl(`games/${ApplicationGameConfig.gameId}`) || '';

    this.container.registerValue(
      SLOT_GAME_SETTINGS_TOKEN,
      new SlotGameSettings(resourceUrl, this.slotSettings),
      { lifetime: InstanceLifetime.Singleton }
    );
  }

  protected async initPreloader(): Promise<void> {
    this.preloader = this.container.resolve<ILoadingScreen>(LOADING_SCREEN_TOKEN);

    await this.preloader.render(this.rootScene);
    this.preloader.addSteps(5);
  }

  private async initScaleManager(): Promise<void> {
    const coordinateSystemInfoProvider = this.container.resolve<CoordinateSystemInfoProvider>(
      T_ICoordinateSystemInfoProvider
    );

    const scaleManager = new ScaleManager(
      coordinateSystemInfoProvider,
      new RootCanvasManager(),
      this.container.resolve(T_Platform)
    );

    this.container.registerValue(T_ScaleManager, scaleManager, {
      lifetime: InstanceLifetime.Singleton,
    });

    const coordinateSystem = coordinateSystemInfoProvider.coordinateSystem;

    const bgMinPos = new Vector2(0.0, 0.0);
    const bgMinSize = coordinateSystemInfoProvider.coordinateSystem.size;

    const bgMaxPos = new Vector2(0.0, 0.0);
    const bgMaxSize = coordinateSystemInfoProvider.coordinateSystem.size;

    await scaleManager.scaler.initialize(
      [
        new ScaleEntry(
          new Rectangle(bgMinPos.x, bgMinPos.y, bgMinSize.x, bgMinSize.y),
          new Rectangle(bgMaxPos.x, bgMaxPos.y, bgMaxSize.x, bgMaxSize.y),
          true,
          true
        ),
      ],
      new Rectangle(0.0, 0.0, coordinateSystem.width, coordinateSystem.height),
      Stretch.Uniform
    );

    scaleManager.scaler.addScaleChangedListener((info) => {
      this.rootScene.scale = info.scale;
      this.rootScene.position = info.position;
      TemplateApplication.scale = info.scale;
    });

    scaleManager.scaler.invalidate();
  }

  public async loadResources(): Promise<void> {
    console.log('loadResources');

    const authorizationHolder =
      this.container.resolve<IAuthorizationHolder>(T_IAuthorizationHolder);

    const session = ApplicationUserConfig.session || '';
    const id = ApplicationUserConfig.userId || '';
    const userId = parseFloat(id || '0');

    authorizationHolder.setAuthorization(session, id, userId);

    const gameDataService = this.container.resolve<GameDataService>(GAME_DATA_SERVICE_TOKEN);
    await gameDataService.loadUser();
  }

  public async loadGameData(): Promise<void> {
    this.container.registerValue(
      SCENE_COMMON_TOKEN,
      new SceneCommon(
        this.container.resolve<SceneFactory>(SCENE_FACTORY_TOKEN),
        this.container.resolve<CustomGamesResourceCache>(T_ResourceCache)
      ),
      { lifetime: InstanceLifetime.Singleton }
    );

    this.container.register(STORAGE_SERVICE_TOKEN, () => new LocalStorageService(), {
      lifetime: InstanceLifetime.Singleton,
    });

    this.container.register(
      STORAGE_MARSHAL_SERVICE_TOKEN,
      (container) => new StorageMarshalBaseService(container),
      {
        lifetime: InstanceLifetime.Singleton,
      }
    );

    this.container.register(APP_SETTINGS_SERVICE_TOKEN, (c) => new AppSettingsService(c), {
      lifetime: InstanceLifetime.Singleton,
    });

    const game = this.gameFactory(ApplicationGameConfig.machineId);
    game.initialize();

    this.gameView = (await game.slotLoaded) as BaseReelsSlotGame;
    await this.preloader.finish();
  }

  public async onAppReady() {
    this.rootScene.addChild(this.gameView!.gameNode);
    this.preloader.deinitialize();

    for (const child of this.rootScene.childs) {
      if (child != this.gameView!.gameNode) {
        child.parent?.removeChild(child);
        child.deinitialize();
      }
    }

    this.gameView!.start();
  }

  public onInitError(error: unknown): void {
    console.error(error);
    // throw new Error('Method not implemented.');
  }

  public onError(error: unknown): void {
    console.error(error);
    // throw new Error('Method not implemented.');
  }

  public activate(active: boolean): void {
    if (!active) {
      this.platform.audioSystem.pause();
    } else {
      const resumeGamePopup = this.container.resolve<IResumeGamePopupComponent>(
        RESUME_GAME_POPUP_COMPONENT_TOKEN,
        { optional: true }
      );
      this.platform.audioSystem.resume(resumeGamePopup || undefined);
    }
  }
}
